import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ConfigService } from '@app/core/config/config.service';
import { Router } from '@angular/router';

const BO_ACCESS_KEY = 'bo_access';
const BO_REFRESH_KEY = 'bo_refresh';
const BO_MFA_TOKEN_KEY = 'bo_mfa_token';
const BO_USER_KEY = 'bo_user';
const BO_RECOVERY_EMAIL_KEY = 'bo_recovery_email';
const BO_RECOVERY_PHONE_KEY = 'bo_recovery_phone';
const BO_RECOVERY_FLOW_KEY = 'bo_recovery_flow';
const BO_LOGIN_PATH = '/internal/ops/v2/review/compliance/login';
const BO_HOME_PATH = '/internal/ops/v2/review/compliance/manage-requests';

@Injectable({ providedIn: 'root' })
export class BackOfficeAuthService {
  private baseUrl: string;
  private isAuthenticated$ = new BehaviorSubject<boolean>(this.hasValidToken());

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private router: Router
  ) {
    this.baseUrl = `${this.configService.baseUrl}api/users`;
  }

  // ─── Token management ─────────────────────────────────────

  getAccessToken(): string | null {
    return sessionStorage.getItem(BO_ACCESS_KEY);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(BO_REFRESH_KEY);
  }

  getMfaToken(): string | null {
    return sessionStorage.getItem(BO_MFA_TOKEN_KEY);
  }

  getUser(): any {
    const raw = sessionStorage.getItem(BO_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  hasValidToken(): boolean {
    return !!sessionStorage.getItem(BO_ACCESS_KEY);
  }

  isLoggedIn(): Observable<boolean> {
    return this.isAuthenticated$.asObservable();
  }

  private storeTokens(access: string, refresh?: string): void {
    sessionStorage.setItem(BO_ACCESS_KEY, access);
    if (refresh) sessionStorage.setItem(BO_REFRESH_KEY, refresh);
    this.isAuthenticated$.next(true);
  }

  private storeUser(user: any): void {
    sessionStorage.setItem(BO_USER_KEY, JSON.stringify(user));
  }

  storeMfaToken(mfaToken: string): void {
    sessionStorage.setItem(BO_MFA_TOKEN_KEY, mfaToken);
  }

  storeRecoveryIdentity(email: string, phone: string): void {
    sessionStorage.setItem(BO_RECOVERY_EMAIL_KEY, email);
    sessionStorage.setItem(BO_RECOVERY_PHONE_KEY, phone);
    sessionStorage.setItem(BO_RECOVERY_FLOW_KEY, 'true');
  }

  getRecoveryIdentity(): { email: string; phone: string } {
    return {
      email: sessionStorage.getItem(BO_RECOVERY_EMAIL_KEY) || '',
      phone: sessionStorage.getItem(BO_RECOVERY_PHONE_KEY) || '',
    };
  }

  isInRecoveryFlow(): boolean {
    return sessionStorage.getItem(BO_RECOVERY_FLOW_KEY) === 'true';
  }

  clearSession(): void {
    sessionStorage.removeItem(BO_ACCESS_KEY);
    sessionStorage.removeItem(BO_REFRESH_KEY);
    sessionStorage.removeItem(BO_MFA_TOKEN_KEY);
    sessionStorage.removeItem(BO_USER_KEY);
    sessionStorage.removeItem(BO_RECOVERY_EMAIL_KEY);
    sessionStorage.removeItem(BO_RECOVERY_PHONE_KEY);
    sessionStorage.removeItem(BO_RECOVERY_FLOW_KEY);
    this.isAuthenticated$.next(false);
  }

  private authHeaders(): HttpHeaders {
    const token = this.getAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    });
  }

  // ─── 1. POST /register/ — Crear usuario (SUPERADMIN) ─────

  register(data: {
    email: string;
    user_name: string;
    phone_number?: string;
    first_name?: string;
    last_name?: string;
    password?: string;
    role?: string;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/register/`, data, {
      headers: this.authHeaders(),
    });
  }

  // ─── 2. POST /auth/login/ — Login email + password ────────

  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login/`, { email, password }).pipe(
      tap((res: any) => {
        const raw = res?.data ?? res;
        if (raw?.access) {
          this.storeTokens(raw.access, raw.refresh);
          if (raw.user) this.storeUser(raw.user);
        }
        if (raw?.mfa_token) {
          this.storeMfaToken(raw.mfa_token);
        }
      })
    );
  }

  // ─── 3. POST /auth/logout/ — Cerrar sesión ───────────────

  /** Cierra sesion en backend y limpia estado local + navega a login.
   * Usa finalize para garantizar cleanup aun si el backend responde error
   * (ej. token ya expirado): el intent del usuario se respeta siempre. */
  logout(): Observable<any> {
    const refresh = this.getRefreshToken();
    const body = refresh ? { refresh } : {};
    return this.http.post(`${this.baseUrl}/auth/logout/`, body, {
      headers: this.authHeaders(),
    }).pipe(
      finalize(() => {
        this.clearSession();
        this.router.navigate([BO_LOGIN_PATH]);
      })
    );
  }

  // ─── 4. POST /auth/refresh/ — Renovar access token ───────

  refreshToken(): Observable<any> {
    const refresh = this.getRefreshToken();
    return this.http.post(`${this.baseUrl}/auth/refresh/`, { refresh }).pipe(
      tap((res: any) => {
        const raw = res?.data ?? res;
        if (raw?.access) {
          sessionStorage.setItem(BO_ACCESS_KEY, raw.access);
        }
      })
    );
  }

  // ─── 5. POST /auth/force-logout/ — Forzar cierre ─────────

  forceLogout(email: string, password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/force-logout/`, { email, password });
  }

  // ─── 6. POST /auth/change-password/ — Primer cambio ──────

  changePassword(newPassword: string, newPasswordConfirm: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/change-password/`, {
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    }, { headers: this.authHeaders() });
  }

  // ─── 7. POST /auth/2fa/generate/ — Generar QR URI ────────

  generate2FA(): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/2fa/generate/`, {}, {
      headers: this.authHeaders(),
    });
  }

  // ─── 8. POST /auth/2fa/enable/ — Activar 2FA ─────────────

  enable2FA(totpCode: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/2fa/enable/`, {
      totp_code: totpCode,
    }, { headers: this.authHeaders() });
  }

  // ─── 9. POST /auth/2fa/verify/ — Verificar MFA ───────────

  verify2FA(mfaToken: string, totpCode: string, flowType: 'login' | 'reset_password' = 'login'): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/2fa/verify/`, {
      mfa_token: mfaToken,
      totp_code: totpCode,
      flow_type: flowType,
    }).pipe(
      tap((res: any) => {
        const raw = res?.data ?? res;
        if (raw?.access) {
          this.storeTokens(raw.access, raw.refresh);
          if (raw.user) this.storeUser(raw.user);
        }
        sessionStorage.removeItem(BO_MFA_TOKEN_KEY);
      })
    );
  }

  // ─── 10. POST /auth/2fa/verify-code/ — Validar TOTP ──────

  verifyTotpCode(totpCode: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/2fa/verify-code/`, {
      totp_code: totpCode,
    }, { headers: this.authHeaders() });
  }

  // ─── 11. POST /auth/verify-identity/ — Recuperación paso 1

  verifyIdentity(email: string, phoneNumber: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/verify-identity/`, {
      email,
      phone_number: phoneNumber,
    });
  }

  // ─── 12. POST /auth/send-verification-code/ — Enviar OTP ─

  sendVerificationCode(identifier: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/send-verification-code/`, {
      identifier,
    }, { headers: this.authHeaders() });
  }

  // ─── 13. POST /auth/reset-password/ — Resetear password ──

  resetPassword(code: string, password: string, passwordConfirm: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/reset-password/`, {
      code,
      password,
      password_confirm: passwordConfirm,
    }, { headers: this.authHeaders() });
  }

  // ─── 14. POST /auth/forgot-password/ — Placeholder ───────

  forgotPassword(): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/forgot-password/`, {});
  }

  // ─── Helpers de navegación ────────────────────────────────

  navigateToLogin(): void {
    this.router.navigate([BO_LOGIN_PATH]);
  }

  navigateToHome(): void {
    this.router.navigate([BO_HOME_PATH]);
  }
}
